import { useState, useCallback } from 'react';
import { useArticles } from './hooks/useArticles';
import { useTyping } from './hooks/useTyping';
import { ArticleList } from './components/ArticleList';
import { ArticleEditor } from './components/ArticleEditor';
import { TypingArea } from './components/TypingArea';
import { StatsPanel } from './components/StatsPanel';
import { HistoryList } from './components/HistoryList';
import { Article } from './types';
import './App.css';

function App() {
  const {
    articles,
    loading,
    selectedArticle,
    addArticle,
    updateArticle,
    deleteArticle,
    selectArticle,
  } = useArticles();

  const {
    state,
    stats,
    setInputRef,
    handleLineInput,
    reset,
  } = useTyping(selectedArticle);

  const [editorOpen, setEditorOpen] = useState(false);
  const [editingArticle, setEditingArticle] = useState<Article | null>(null);
  const [historyRefresh, setHistoryRefresh] = useState(0);

  const handleAddArticle = useCallback(() => {
    setEditingArticle(null);
    setEditorOpen(true);
  }, []);

  const handleEditArticle = useCallback((article: Article) => {
    setEditingArticle(article);
    setEditorOpen(true);
  }, []);

  const handleSaveArticle = useCallback(
    async (title: string, content: string) => {
      if (editingArticle) {
        await updateArticle(editingArticle.id, title, content);
      } else {
        await addArticle(title, content);
      }
    },
    [editingArticle, addArticle, updateArticle]
  );

  const handleTypingReset = useCallback(() => {
    reset();
    setHistoryRefresh((prev) => prev + 1);
  }, [reset]);

  if (loading) {
    return (
      <div className="app">
        <div className="loading-container">
          <div className="spinner"></div>
          <p>加载中...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="app">
      <header className="app-header">
        <div className="logo">
          <span className="logo-icon">⌨️</span>
          <h1>OpenType</h1>
        </div>
        <div className="header-actions">
          <HistoryList refreshTrigger={historyRefresh} />
          <button className="btn-primary" onClick={handleAddArticle}>
            + 添加文章
          </button>
        </div>
      </header>

      <main className="app-main">
        <div className="main-content">
          <div className="typing-section">
            <TypingArea
              article={selectedArticle}
              state={state}
              stats={stats}
              setInputRef={setInputRef}
              onLineInput={handleLineInput}
              onReset={handleTypingReset}
            />
          </div>

          <div className="sidebar-section">
            <StatsPanel stats={stats} />
            <ArticleList
              articles={articles}
              selectedArticle={selectedArticle}
              onSelect={selectArticle}
              onEdit={handleEditArticle}
              onDelete={deleteArticle}
            />
          </div>
        </div>
      </main>

      <ArticleEditor
        article={editingArticle}
        isOpen={editorOpen}
        onClose={() => setEditorOpen(false)}
        onSave={handleSaveArticle}
      />

      <footer className="app-footer">
        <p>
          提示: 按 Tab 键可快速重新开始练习 | 数据保存在本地浏览器中
        </p>
      </footer>
    </div>
  );
}

export default App;
